import "./style.scss";
import { FC, MouseEvent, useEffect, useState } from "react";
import ModalPortal from "@/src/components/ModalPortal/ModalPortal";
import EditorCloseSvg from "@/src/svg/applicationsPage/APPopUp/EditorCloseSvg";
import DocumentPreviewPlaceholder from "@/src/components/CustomUtils/DocumentPreviewPlaceholder/DocumentPreviewPlaceholder";
import {
  getDocumentPreviewKind,
  getDocumentTypeLabel,
  resolveFileExtension,
} from "@/src/utils/documentPreviewUtils";

export interface CustomDocumentPreviewModalProps {
  isOpen: boolean;
  documentUrl: string | null;
  onClose: () => void;
  onApprove?: () => void;
  onReject?: () => void;
  title?: string;
  fileExtension?: string;
  fileName?: string;
}

const CustomDocumentPreviewModal: FC<CustomDocumentPreviewModalProps> = ({
  isOpen,
  documentUrl,
  onClose,
  onApprove,
  onReject,
  title = "Document preview",
  fileExtension,
  fileName,
}) => {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [documentUrl, isOpen]);

  if (!isOpen || !documentUrl) return null;

  const extension = resolveFileExtension(fileExtension, fileName, documentUrl);
  const previewKind = getDocumentPreviewKind(extension);
  const typeLabel = getDocumentTypeLabel(extension);
  const showActions = Boolean(onApprove || onReject);

  const handleOverlayClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const renderPreviewContent = () => {
    if (previewKind === "image" && !imageFailed) {
      return (
        <img
          src={documentUrl}
          alt={title}
          className="custom-document-preview-modal-image"
          onError={() => setImageFailed(true)}
        />
      );
    }

    if (previewKind === "pdf") {
      return <iframe src={documentUrl} className="custom-document-preview-modal-frame" title={title} />;
    }

    if (previewKind === "image" && imageFailed) {
      return <DocumentPreviewPlaceholder label="IMG" />;
    }

    return <DocumentPreviewPlaceholder label={typeLabel} />;
  };

  return (
    <ModalPortal>
      <div className="custom-document-preview-modal" onClick={handleOverlayClick}>
        <button
          type="button"
          className="custom-document-preview-modal-close"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label="Close preview"
        >
          <EditorCloseSvg />
        </button>

        <div className="custom-document-preview-modal-content" onClick={(e) => e.stopPropagation()}>
          {renderPreviewContent()}
        </div>

        {showActions && (
          <div className="custom-document-preview-modal-actions" onClick={(e) => e.stopPropagation()}>
            {onApprove && (
              <button type="button" className="custom-document-preview-modal-action approve" onClick={onApprove}>
                Approved
              </button>
            )}
            {onReject && (
              <button type="button" className="custom-document-preview-modal-action reject" onClick={onReject}>
                Reject
              </button>
            )}
          </div>
        )}
      </div>
    </ModalPortal>
  );
};

export default CustomDocumentPreviewModal;
